"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PlayerAvatar } from "@/components/shared/player-avatar";
import type { VysledokHraca } from "@/types/database";
import {
  getHracId,
  getHracMeno,
  getPozicia,
  getKlub,
  getPocetHlasov,
} from "@/types/database";
import { Shield, Users, Target, Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface AgregatedRosterProps {
  vysledky: VysledokHraca[];
}

export function AgregatedRoster({ vysledky }: AgregatedRosterProps) {
  // Zoradenie podľa pozícií
  const brankari = vysledky
    .filter((h) => getPozicia(h) === "brankar")
    .sort((a, b) => getPocetHlasov(b) - getPocetHlasov(a));

  const obrancovia = vysledky
    .filter((h) => getPozicia(h) === "obranca")
    .sort((a, b) => getPocetHlasov(b) - getPocetHlasov(a));

  const utocnici = vysledky
    .filter((h) => getPozicia(h) === "utocnik")
    .sort((a, b) => getPocetHlasov(b) - getPocetHlasov(a));

  const getPoziciaLabel = (pozicia: string) => {
    switch (pozicia) {
      case "brankar":
        return "G";
      case "obranca":
        return "D";
      case "utocnik":
        return "F";
      default:
        return pozicia;
    }
  };

  const PlayerRow = ({
    hrac,
    index,
  }: {
    hrac: VysledokHraca;
    index: number;
  }) => (
    <div
      className={cn(
        "flex flex-col sm:flex-row sm:items-center justify-between",
        "py-2 sm:py-3 border-b border-muted/30 last:border-0 hover:bg-muted/10 px-2 sm:px-3 transition-colors gap-2 sm:gap-3",
      )}
    >
      <div className="flex items-center gap-3">
        <span className="text-xs font-mono text-muted-foreground w-4">
          {index + 1}.
        </span>
        <div className="flex items-center gap-2">
          <PlayerAvatar
            hracId={getHracId(hrac)}
            meno={getHracMeno(hrac)}
            pozicia={getPozicia(hrac)}
            className="h-8 w-8 border border-muted"
            fallbackClassName="text-xs"
          />
          <div className="flex flex-col">
            <span className="text-sm font-semibold leading-none">
              {getHracMeno(hrac)}
            </span>
            <span className="text-xs text-muted-foreground uppercase font-bold">
              {getKlub(hrac) || "Bez klubu"}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-4 ml-7 sm:ml-0">
        <div className="flex flex-col items-end">
          <Badge
            variant="outline"
            className="text-xs py-0 h-5 border-muted/60 text-muted-foreground uppercase tracking-wider"
          >
            {getPoziciaLabel(getPozicia(hrac))}
          </Badge>
          <div className="flex items-center gap-1 mt-0.5">
            <Star className="h-3 w-3 text-hockey-gold fill-hockey-gold" />
            <span className="text-xs font-bold">{getPocetHlasov(hrac)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPoziciaSection = (
    title: string,
    hraci: VysledokHraca[],
    icon: React.ReactNode,
    bgColor: string,
  ) => (
    <Card className="border-muted/60 shadow-sm overflow-hidden">
      <CardHeader className={cn(bgColor, "py-3 border-b border-muted/40")}>
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          {icon}
          {title}
          <Badge variant="secondary" className="ml-auto text-xs">
            {hraci.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {hraci.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Žiadni hráči
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {hraci.map((hrac, index: number) => (
              <PlayerRow key={getHracId(hrac)} hrac={hrac} index={index} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {renderPoziciaSection(
        "Brankári",
        brankari,
        <Shield className="h-4 w-4 text-status-info" />,
        "bg-status-info/5",
      )}
      {renderPoziciaSection(
        "Obrancovia",
        obrancovia,
        <Users className="h-4 w-4 text-primary" />,
        "bg-primary/5",
      )}
      {renderPoziciaSection(
        "Útočníci",
        utocnici,
        <Target className="h-4 w-4 text-status-success" />,
        "bg-status-success/5",
      )}
    </div>
  );
}
